import {
  ExternalSystemAdmin,
  CreateExternalSystemAdminRequest,
  UpdateExternalSystemAdminRequest,
  VerticalLiftStatus,
  VerticalLiftEvent,
  VerticalLiftActivityFilter,
  VerticalLiftPendingOrder
} from '../types/externalSystem';
import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/**
 * Admin-service for ExternalSystem (vareheis-konfig) — mønster fra loadingZoneService.
 */
class ExternalSystemServiceClass {
  private getBaseUrl(): string {
    const userContext = AuthService.getUserDataFromToken();
    if (!userContext) {
      throw new Error('No user context available');
    }
    return `${API_BASE_URL}/${userContext.companyNo}/externalsystems`;
  }

  private async throwOnError(response: Response): Promise<void> {
    if (response.ok) return;
    const errorText = await response.text();
    try {
      const parsed = JSON.parse(errorText);
      if (parsed?.error) throw new Error(parsed.error);
    } catch (e) {
      if (e instanceof Error && e.message && !(e instanceof SyntaxError)) throw e;
    }
    throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
  }

  async getExternalSystems(): Promise<ExternalSystemAdmin[]> {
    const response = await AuthService.authenticatedGet(this.getBaseUrl());
    await this.throwOnError(response);
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  async createExternalSystem(request: CreateExternalSystemAdminRequest): Promise<ExternalSystemAdmin> {
    const response = await AuthService.authenticatedPost(this.getBaseUrl(), request);
    await this.throwOnError(response);
    return await response.json();
  }

  async updateExternalSystem(id: number, request: UpdateExternalSystemAdminRequest): Promise<ExternalSystemAdmin> {
    const response = await AuthService.authenticatedPut(`${this.getBaseUrl()}/${id}`, request);
    await this.throwOnError(response);
    return await response.json();
  }

  async deleteExternalSystem(id: number): Promise<void> {
    const response = await AuthService.authenticatedFetch(`${this.getBaseUrl()}/${id}`, { method: 'DELETE' });
    await this.throwOnError(response);
  }

  async getLiftStatus(id: number): Promise<VerticalLiftStatus> {
    const response = await AuthService.authenticatedGet(`${this.getBaseUrl()}/${id}/status`);
    await this.throwOnError(response);
    return await response.json();
  }

  async getLiftPendingOrders(id: number): Promise<VerticalLiftPendingOrder[]> {
    const response = await AuthService.authenticatedGet(`${this.getBaseUrl()}/${id}/pending`);
    await this.throwOnError(response);
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  async getLiftActivity(id: number, filter: VerticalLiftActivityFilter = {}): Promise<VerticalLiftEvent[]> {
    const params = new URLSearchParams();
    if (filter.from) params.set('from', filter.from);
    if (filter.flow) params.set('flow', filter.flow);
    if (filter.eventType) params.set('eventType', filter.eventType);
    if (filter.onlyErrors) params.set('onlyErrors', 'true');
    if (filter.take) params.set('take', String(filter.take));
    const query = params.toString();
    const response = await AuthService.authenticatedGet(`${this.getBaseUrl()}/${id}/activity${query ? `?${query}` : ''}`);
    await this.throwOnError(response);
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }
}

export const externalSystemService = new ExternalSystemServiceClass();
